// Velocity Offshore Holdings — Organization Structure (Rev 1.0, May 2026)
// Sourced from the official org-chart deck. Used by the interactive
// org chart on the Intranet → Org Chart page.

export type OrgNode = {
  id: string;
  title: string;            // Role / seat name
  name?: string;            // Named incumbent if any
  scope?: string;           // Short scope tagline (e.g. "OPERATIONS · TECHNICAL · CREWING")
  spine?: string;           // Functional spine label
  division?: "I" | "II" | null;
  flags?: string[];         // e.g. ["Primary DPA", "CSO"], ["Alternate DPA"]
  responsibilities?: string[];
  reportsToCEO?: boolean;   // dotted/independent line indicator
  phaseNote?: string;       // phasing context if relevant
  children?: OrgNode[];
};

export const orgChart: OrgNode = {
  id: "ceo",
  title: "Chief Executive Officer",
  name: "Jerome Arnold",
  scope: "Board · Strategy · Capital",
  responsibilities: [
    "Sets group strategy and capital allocation across both divisions",
    "Owns board reporting and shareholder engagement",
    "Holds the independent line from the HSE Manager (Alternate DPA)",
  ],
  children: [
    {
      id: "coo",
      title: "Chief Operating Officer",
      name: "Kyle Pemberton",
      scope: "Operations · Technical · Crewing",
      spine: "Operations Spine",
      flags: ["Primary DPA", "CSO"],
      responsibilities: [
        "Owns fleet, technical and crewing across both divisions",
        "Holds the ISM Code Designated Person Ashore (DPA) function across the group",
        "Company Security Officer (CSO) under the ISPS Code",
        "Approves EAR/MAR projects up to USD 50,000 (above threshold escalates to COO review)",
      ],
      children: [
        {
          id: "marine-ops-dir",
          title: "Marine Operations Director",
          name: "Eduard Ercegovic",
          scope: "Fleet · Voyage · Marine Assurance",
          responsibilities: [
            "Day-to-day authority and budget to act on safety and assurance findings",
            "Supports the COO (Primary DPA / CSO) on shipboard implementation of the SMS and SSP",
          ],
        },
        { id: "tech-dir", title: "Technical Director", scope: "Class · Dry-Dock · Newbuild",
          responsibilities: [
            "Group technical authority across HiCap and AIRCAT fleets",
            "Final technical approver in the EAR/MAR chain below COO threshold",
          ],
        },
        { id: "crew-dir", title: "Crewing & HR Director", scope: "Manning · STCW · Welfare",
          responsibilities: [
            "Manning plans, STCW compliance, rotational schedules",
            "Crew welfare and MLC 2006 oversight",
          ],
        },
        { id: "mav", title: "Marine Assurance & Vetting", scope: "OVMSA · OVIQ · TMSA",
          responsibilities: [
            "Owns OVMSA2 (global) and OVIQ4 (per-vessel) audit readiness",
            "Liaises with charterer vetting teams",
          ],
        },
        { id: "div-1", title: "Division I — HiCap Chemical Carriage", division: "I",
          scope: "UT 7422 IBC HiCap · Type 1 · Methanol Dual-Fuel · ~84.9 m LOA",
          responsibilities: [
            "Coastal and regional chemical/product carriage for upstream operators",
            "Compliant with the IBC Code and OSV Chemical Code",
          ],
          children: [
            { id: "div1-cargo-super", title: "Chemical Cargo Superintendent", division: "I", scope: "IBC · Tank Cleaning" },
            { id: "div1-masters", title: "HiCap Vessel Masters", division: "I", scope: "Sea-going · per hull" },
            { id: "div1-cargo-officers", title: "Chief Officers / Cargo Officers", division: "I", scope: "Cargo Plan · COW" },
            { id: "div1-ce", title: "Chief Engineers — Dual-Fuel", division: "I", scope: "Methanol · IGF Code" },
            { id: "div1-tech", title: "Technical Superintendent — HiCap", division: "I", scope: "Shore · Dry-Dock" },
            { id: "div1-crewing", title: "Crewing Coordinator — HiCap", division: "I", scope: "STCW V/1-1 Chem" },
          ],
        },
        { id: "div-2", title: "Division II — AIRCAT IP Transfer", division: "II",
          scope: "IP Code · DP2 · ABS-Classed · RMI-Flagged · Crewliner & Combi",
          responsibilities: [
            "Industrial personnel transfers and crew change logistics on the AIRCAT 35",
            "Services FPSOs, MODUs and offshore renewables sites",
          ],
          children: [
            { id: "div2-ops-super", title: "AIRCAT Operations Superintendent", division: "II", scope: "IP Code Compliance" },
            { id: "div2-masters", title: "AIRCAT Vessel Masters", division: "II", scope: "DP2 · per hull" },
            { id: "div2-dp", title: "DP / Marine Ops Specialist", division: "II", scope: "Kongsberg DP2" },
            { id: "div2-pax", title: "Passenger Safety & W2W Lead", division: "II", scope: "Transfers · Briefings" },
            { id: "div2-tech", title: "Technical Superintendent — AIRCAT", division: "II", scope: "Shore · Class" },
            { id: "div2-crewing", title: "Crewing Coordinator — AIRCAT", division: "II", scope: "Rotational · Local" },
          ],
        },
      ],
    },
    {
      id: "cco",
      title: "Chief Commercial Officer",
      name: "Bo Jardine",
      scope: "Sales · Chartering · BD",
      spine: "Commercial Spine",
      responsibilities: [
        "Two divisional BD seats — HiCap and AIRCAT have separate buyer personas and sales cycles",
        "Owns chartering, post-fixture and tendering",
        "Phase 1: also holds the Chartering Manager seat",
      ],
      children: [
        { id: "bd-dir", title: "BD Director", scope: "Anchor Clients · Tenders" },
        { id: "chartering", title: "Chartering Manager", scope: "CP Negotiation · BIMCO" },
        { id: "bd-chem", title: "BD Manager — Chemical / Tanker", division: "I", scope: "HiCap" },
        { id: "voyage-ops", title: "Voyage Operator", scope: "Post-Fixture · Bunkers" },
        { id: "bd-aircat", title: "BD Manager — Crew Transfer", division: "II", scope: "AIRCAT RFQ · Pricing" },
        { id: "tenders", title: "Crew & IP Tender & Proposals", scope: "Bid Management" },
        { id: "liaison", title: "Account & Ops Liaison", scope: "Customer Success" },
        { id: "marketing", title: "Marketing & Comms", scope: "Brand · VOFF Digest" },
      ],
    },
    {
      id: "cfo",
      title: "Chief Financial Officer",
      name: "To be appointed",
      scope: "Finance · IT · Procurement",
      spine: "Finance & Admin",
      phaseNote: "Phase 1: CFO can be fractional; the Controller fills the day-to-day gap.",
      children: [
        { id: "controller", title: "Financial Controller", scope: "Reporting · Audit" },
        { id: "procurement", title: "Procurement & Supply Chain Manager", scope: "Spares · Logistics" },
        { id: "it", title: "IT & Digital Systems Manager", scope: "ERP · SMS · Cyber" },
        { id: "office", title: "Office & Shore-Base Manager", scope: "Houston · Georgetown · Luanda" },
        { id: "treasury", title: "Treasury / AR", scope: "Cash · Collections" },
        { id: "ap", title: "Accounts Payable", scope: "Vendors · Invoices" },
      ],
    },
    {
      id: "gc",
      title: "General Counsel",
      name: "Fractional / External",
      scope: "Legal · Compliance · Risk",
      spine: "Legal",
      responsibilities: ["External / fractional engagement", "Company Secretary duties"],
    },
    {
      id: "hse",
      title: "HSE Manager",
      name: "To be appointed",
      scope: "HSE · Internal Audit",
      spine: "HSE Function",
      flags: ["Alternate DPA", "Independent line to CEO"],
      reportsToCEO: true,
      responsibilities: [
        "Reports directly to the CEO — structurally independent of Operations",
        "Owns internal audit and the assurance line, including the Primary DPA's function",
        "Findings cannot be buried inside Operations — checks and balances by structure",
      ],
      children: [
        { id: "auditor", title: "Internal Auditor", scope: "ISM · ISPS · MLC" },
        { id: "training", title: "Training & Competency", scope: "STCW · Drills" },
        { id: "qhse", title: "QHSE Coordinator", scope: "Document Control" },
        { id: "env", title: "Environmental Compliance", scope: "MARPOL · ESG" },
      ],
    },
  ],
};

// Phased hiring sequence — surfaced on the org chart page.
export const orgPhases = [
  {
    id: "p1",
    label: "Phase 1",
    horizon: "≤ 12 months",
    title: "Founder-Led, HSE-First",
    headcount: "~ 8 shore staff",
    points: [
      "HSE Manager appointed immediately — Alternate DPA, reports directly to CEO, owns internal audit and assurance.",
      "COO (Kyle Pemberton) holds the Primary DPA / CSO role across the group.",
      "CCO holds Chartering Manager seat. CFO is fractional / outsourced controller. General Counsel external.",
      "Fleet Managers report directly to COO. Senior Tech Super is first technical hire.",
      "Two BD heads (Chemical and AIRCAT) hired early to drive separate sales motions.",
    ],
  },
  {
    id: "p2",
    label: "Phase 2",
    horizon: "12–24 months",
    title: "Functional Separation",
    headcount: "~ 16 shore staff",
    points: [
      "Dedicated Director of Marine Operations appointed under the COO to own day-to-day fleet execution.",
      "COO (Kyle Pemberton) retains the Primary DPA / CSO role per ISM Code §4 and ISPS Code.",
      "Appoint a full-time CFO; bring procurement and IT in-house.",
      "Localise Crewing Coordinators to Georgetown and Luanda shore bases.",
      "Voyage Operator and Tender & Proposals seats added under Commercial.",
    ],
  },
  {
    id: "p3",
    label: "Phase 3",
    horizon: "24+ months",
    title: "Divisional P&L Owners",
    headcount: "~ 28 shore staff",
    points: [
      "HiCap GM and AIRCAT GM appointed as divisional P&L owners.",
      "Each division gains its own dedicated commercial and technical leads.",
      "HSE Manager retains independent CEO line across both divisions; SMS stays single-coded.",
      "Office & Shore-Base Mgr expands to a regional GM structure as fleet scales.",
      "Board reporting matures with divisional dashboards and SVIP integration.",
    ],
  },
];
